import type { Feature, FeatureCollection, Point } from 'geojson'

import { SERVICE_CALIB_ICON, getServiceCalibIconId } from './serviceCalibMapIcons'
import type { ServiceCalibCatalogItem } from './serviceCalibTypes'

export type ServiceCalibMapFeatureProps = {
  key: string
  iconId: string
  mapKind: 'station' | 'lot' | 'maintenance'
  highlighted: number
  /** 1 when a catalog row is selected — enables dimming non-selected pins. */
  selectionActive: number
  /** Display name for hover tooltip (station / lot / MP). */
  name: string
}

export function buildServiceCalibMapFeatures(
  items: ServiceCalibCatalogItem[],
  selectedKey: string | null
): FeatureCollection<Point, ServiceCalibMapFeatureProps> {
  const stationLots = new Map<string, ServiceCalibCatalogItem[]>()
  const maintenanceItems: ServiceCalibCatalogItem[] = []

  items.forEach(item => {
    if (item.point?.type !== 'Point' || item.point.coordinates?.length !== 2) {
      return
    }
    if (item.target === 'maintenance_point') {
      maintenanceItems.push(item)
      return
    }
    if (item.target === 'station_lot' && item.stationHardwareId) {
      const group = stationLots.get(item.stationHardwareId) ?? []
      group.push(item)
      stationLots.set(item.stationHardwareId, group)
    }
  })

  const features: Array<Feature<Point, ServiceCalibMapFeatureProps>> = []
  const selectionActive = selectedKey ? 1 : 0

  stationLots.forEach(lots => {
    const sorted = [...lots].sort((a, b) => (a.lotId ?? 0) - (b.lotId ?? 0))
    const point = sorted[0].point
    if (!point?.coordinates) {
      return
    }
    const lotKeys = sorted.map(l => l.key)
    const highlighted = selectedKey && lotKeys.includes(selectedKey) ? 1 : 0
    const selectKey = highlighted && selectedKey ? selectedKey : sorted[0].key

    if (sorted.length > 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: point.coordinates as [number, number] },
        properties: {
          key: selectKey,
          iconId: SERVICE_CALIB_ICON.STATION,
          mapKind: 'station',
          highlighted,
          selectionActive,
          name: sorted[0].stationName || sorted.map(l => l.name).filter(Boolean).join(', ') || 'Station',
        },
      })
      return
    }

    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: point.coordinates as [number, number] },
      properties: {
        key: sorted[0].key,
        iconId: getServiceCalibIconId(sorted[0]),
        mapKind: 'lot',
        highlighted: sorted[0].key === selectedKey ? 1 : 0,
        selectionActive,
        name: sorted[0].name || sorted[0].stationName || 'Lot',
      },
    })
  })

  maintenanceItems.forEach(item => {
    if (!item.point?.coordinates) {
      return
    }
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: item.point.coordinates as [number, number] },
      properties: {
        key: item.key,
        iconId: getServiceCalibIconId(item),
        mapKind: 'maintenance',
        highlighted: item.key === selectedKey ? 1 : 0,
        selectionActive,
        name: item.name || 'Maintenance point',
      },
    })
  })

  return features.length ? { type: 'FeatureCollection', features } : { type: 'FeatureCollection', features: [] }
}
